import {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import { Spinner } from '@/shared/ui/Spinner'
import s from './Toast.module.css'

/**
 * Тона исхода: `success` — получилось, `danger` — не получилось, `warning` — получилось
 * с последствием, `accent` — просто факт. Имена совпадают с `Alert`, `StatusBadge` и `Chip`.
 */
export type ToastStatusTone = 'accent' | 'success' | 'warning' | 'danger'

/**
 * Плюс `pending` — идущий процесс. Это не «состояние загрузки поверх тона», а собственная
 * палитра: кит рисует его инверсной плашкой со спиннером, без рамки, действия и таймера.
 */
export type ToastTone = ToastStatusTone | 'pending'

/**
 * Живость сообщения, ось из `Alert`. По умолчанию `polite`, а не `off`: алерт отрисован вместе
 * со страницей и пользователь дойдёт до него чтением, тост появился сам посреди чужой работы.
 */
export type ToastAnnounce = 'off' | 'polite' | 'assertive'

/**
 * У инверсной плашки нет ни действия, ни крестика, ни своего глифа — и это запрещено типом,
 * а не подписью в контракте: вложенные кнопки считают цвет из ролей светлой подложки.
 * Разбор в docs/ui-kit/contracts/toast.md.
 */
type ToastToneProps =
  | {
      tone?: ToastStatusTone
      /** Слот глифа в кружке: `Icon size="sm"`. Не задан — глиф берётся по тону. */
      icon?: ReactNode
      /** Слот действия: `Button variant="ghost" size="sm"`. Продлевает жизнь плашки до 8 с. */
      action?: ReactNode
      /** Имя крестика. Вместе с `onClose` — крестик рисуется. Кнопка без имени не собирается. */
      closeLabel?: string
    }
  | { tone: 'pending'; icon?: never; action?: never; closeLabel?: never }

export type ToastProps = {
  /** Текст сообщения, готовым с бэкенда. Пустой — плашка не рисуется вовсе. */
  children: ReactNode
  /** Просьба снять плашку: истёк таймер, нажат крестик, нажат Esc. Снимает — потребитель. */
  onClose?: () => void
  /** Окно жизни в мс. `0` — не исчезает сам. Без `onClose` отсчёт не заводится: звать некого. */
  duration?: number
  /** Внешняя заморозка отсчёта: курсор над стопкой, вкладка в фоне. Считает контейнер. */
  paused?: boolean
  announce?: ToastAnnounce
  ref?: Ref<HTMLDivElement>
  /* role принадлежит announce и потому изъят из ...rest; title — нативный тултип поверх
     исчезающей плашки, который путался бы с самим сообщением. */
} & ToastToneProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'role' | 'title'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<ToastTone, string | undefined> = {
  accent: s.toneAccent,
  success: s.toneSuccess,
  warning: s.toneWarning,
  danger: s.toneDanger,
  pending: s.tonePending,
}

/** Глиф по умолчанию. `danger` и `warning` делят один знак: он про «внимание», не про цвет. */
const TONE_ICON: Record<ToastStatusTone, IconName> = {
  accent: 'info',
  success: 'check',
  warning: 'warn',
  danger: 'warn',
}

/**
 * `alert` перебивает текущую речь, `status` встаёт в очередь. Обе роли неявно дают
 * `aria-atomic="true"`: сообщение и подпись действия читаются одной фразой.
 */
const ROLE: Record<ToastAnnounce, 'alert' | 'status' | undefined> = {
  off: undefined,
  polite: 'status',
  assertive: 'alert',
}

/** Кит: «Живут 4 с, действие „Отменить“ продлевает до 8 с». */
const DURATION_DEFAULT = 4000
const DURATION_WITH_ACTION = 8000

/**
 * Всплывающая плашка об исходе операции: добавлено в корзину, заявка ушла в 1С, связи нет,
 * черновик сохраняется. Появляется сама, живёт 4 секунды (8 — если несёт действие) и уходит.
 *
 * Это **плашка, а не система уведомлений**: очередь, лимит, портал и позиционирование стопки
 * живут в `app` — в манифесте их нет, и здесь они не выдумываются. Таймер, наоборот, живёт
 * тут: только плашка видит указатель и фокус на своём узле, а без остановки отсчёта
 * «Повторить» не успеет прочитать никто (WCAG 2.2.1).
 *
 * Фокус компонент не трогает никогда: тост появляется, пока пользователь набирает ИНН.
 *
 * Контракт: docs/ui-kit/contracts/toast.md
 */
export function Toast({
  children,
  tone = 'accent',
  icon,
  action,
  closeLabel,
  onClose,
  duration,
  paused = false,
  announce = 'polite',
  className,
  ref,
  ...rest
}: ToastProps) {
  const [hovered, setHovered] = useState(false)
  const [focusInside, setFocusInside] = useState(false)

  const hasAction = isFilled(action)

  /*
   * Продление выводится из наличия действия, а не перекладывается на потребителя: тост
   * с «Отменить», исчезающий через 4 секунды, — ловушка, которую можно закрыть умолчанием.
   */
  const fallbackDuration =
    tone === 'pending' ? 0 : hasAction ? DURATION_WITH_ACTION : DURATION_DEFAULT
  const appliedDuration = duration ?? fallbackDuration

  const hasCountdown = appliedDuration > 0 && Boolean(onClose)
  const frozen = paused || hovered || focusInside
  const running = hasCountdown && !frozen

  /*
   * Колбэк живёт в ref: инлайновая стрелка в пропах меняет ссылку на каждый рендер родителя,
   * а перезапуск эффекта на каждом рендере означал бы тост, который не исчезает никогда.
   */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  /*
   * После паузы окно даётся полное, а не остаток: курсор на плашке означает «читаю» или
   * «целюсь в кнопку», и возвращать человеку 300 мс было бы издевательством. Заодно внутри
   * не заводится бухгалтерия остатка. Таймер именно JS: привяжи мы автозакрытие к CSS-анимации,
   * под prefers-reduced-motion она схлопнулась бы в 0.01 мс и тост исчезал бы мгновенно.
   */
  useEffect(() => {
    if (!running) return

    const timer = window.setTimeout(() => {
      onCloseRef.current?.()
    }, appliedDuration)

    return () => {
      window.clearTimeout(timer)
    }
  }, [running, appliedDuration])

  // Пустая плашка поверх работы — визуальный мусор: серверное поле приходит пустым чаще, чем ждут
  if (!isFilled(children)) return null

  // Не isFilled: closeLabel — не слот, а строка, и её пустота проверяется без разбора ReactNode.
  // Крестик без onClose не рисуется: мёртвая кнопка хуже отсутствующей.
  const closable = closeLabel !== undefined && closeLabel !== '' && onClose !== undefined

  const handlePointerEnter = (event: PointerEvent<HTMLDivElement>) => {
    rest.onPointerEnter?.(event)
    // Тап пальцем не должен молча продлевать жизнь плашки: приём из Tooltip
    if (event.pointerType === 'touch') return
    setHovered(true)
  }

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    rest.onPointerLeave?.(event)
    if (event.pointerType === 'touch') return
    setHovered(false)
  }

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    rest.onFocus?.(event)
    setFocusInside(true)
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    rest.onBlur?.(event)
    // Переход фокуса с действия на крестик паузу не снимает: человек всё ещё внутри плашки
    if (event.currentTarget.contains(event.relatedTarget)) return
    setFocusInside(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    rest.onKeyDown?.(event)
    if (event.key !== 'Escape' || event.defaultPrevented || !onClose) return

    // Гасим всплытие только когда действительно закрыли: иначе один Esc унёс бы и модалку
    event.stopPropagation()
    onClose()
  }

  return (
    <div
      {...rest}
      ref={ref}
      className={cx(s.root, TONE_CLASS[tone], className)}
      role={ROLE[announce]}
      data-tone={tone}
      data-announce={announce}
      data-countdown={hasCountdown ? (frozen ? 'paused' : 'running') : undefined}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {/*
       * Спиннер декоративен (без label): о процессе говорит текст плашки. aria-busy на корне
       * не ставится намеренно — на живой области он означает «не объявляй», то есть заглушил бы
       * ровно ту фразу, ради которой плашка появилась.
       */}
      {tone === 'pending' ? (
        <Spinner size="sm" tone="onAccent" />
      ) : (
        <span className={s.marker} aria-hidden="true">
          {isFilled(icon) ? icon : <Icon name={TONE_ICON[tone]} size="sm" />}
        </span>
      )}

      <span className={s.text}>{children}</span>

      {hasAction ? <span className={s.action}>{action}</span> : null}

      {/* Ступень xs и глиф 14px — пара «крестик тоста» из docs/ui-kit/contracts/icon-button.md */}
      {closable ? (
        <IconButton
          icon={<Icon name="close" size="sm" />}
          label={closeLabel}
          variant="ghost"
          size="xs"
          onClick={onClose}
        />
      ) : null}
    </div>
  )
}
